import { HttpApiService } from "@/shared/services/httpApi.service";
import { SHIPPING_QUOTES_API_PATH } from "../constants/quoter-history.constants";
import type {
  ShippingQuote,
  ShippingQuotesListResponse,
  ShippingQuotesQueryParams,
  CreateShippingQuoteRequest,
} from "../types/shipping-quotes-api.types";
import type { QuoterHistoryFiltersState } from "../types/quoter-history.types";

export type FetchShippingQuotesParams = ShippingQuotesQueryParams;

export function buildShippingQuotesQueryParams(
  filters: QuoterHistoryFiltersState,
  page: number,
  pageSize: number
): FetchShippingQuotesParams {
  const skip = (page - 1) * pageSize;
  const params: FetchShippingQuotesParams = { skip, limit: pageSize };

  if (filters.origin_currency) {
    params.origin_currency = filters.origin_currency;
  }
  if (filters.destination_currency) {
    params.destination_currency = filters.destination_currency;
  }
  if (filters.start_date) {
    params.start_date = filters.start_date.includes("T")
      ? filters.start_date
      : `${filters.start_date}T00:00:00.000Z`;
  }
  if (filters.end_date) {
    params.end_date = filters.end_date.includes("T")
      ? filters.end_date
      : `${filters.end_date}T23:59:59.999Z`;
  }

  return params;
}

function buildQueryString(params: FetchShippingQuotesParams): string {
  const search = new URLSearchParams();
  if (params.skip != null) search.set("skip", String(params.skip));
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.origin_currency)
    search.set("origin_currency", params.origin_currency);
  if (params.destination_currency)
    search.set("destination_currency", params.destination_currency);
  if (params.quote_source) search.set("quote_source", params.quote_source);
  if (params.created_by_user_id)
    search.set("created_by_user_id", params.created_by_user_id);
  if (params.start_date) search.set("start_date", params.start_date);
  if (params.end_date) search.set("end_date", params.end_date);
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

function round(value: number, decimals: number): number {
  return Number(value.toFixed(decimals));
}

export const shippingQuotesService = {
  fetchList: async (
    params?: FetchShippingQuotesParams
  ): Promise<ShippingQuotesListResponse> => {
    const url = `${SHIPPING_QUOTES_API_PATH}${buildQueryString(params ?? {})}`;
    const response = await HttpApiService.get<ShippingQuotesListResponse>(url);
    return response.data;
  },

  create: async (
    data: CreateShippingQuoteRequest
  ): Promise<unknown> => {
    const payload = {
      origin_currency: data.origin_currency,
      destination_currency: data.destination_currency,
      amount_to_send: round(Number(data.amount_to_send), 10),
      amount_to_receive: round(Number(data.amount_to_receive), 10),
      commission: round(Number(data.commission), 10),
      ...(data.exchange_rate != null
        ? { exchange_rate: round(Number(data.exchange_rate), 10) }
        : {}),
      quote_source: data.quote_source,
      country_origin: data.country_origin,
      country_destination: data.country_destination,
      valid_until: data.valid_until,
      metadata: data.metadata,
    };

    const response = await HttpApiService.post<unknown, typeof payload>(
      SHIPPING_QUOTES_API_PATH,
      payload,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return response.data;
  },

  getById: async (id: string): Promise<ShippingQuote> => {
    const response = await HttpApiService.get<ShippingQuote>(
      `${SHIPPING_QUOTES_API_PATH}/${id}`
    );
    return response.data;
  },
};
